import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { updateDocumentData } from '../../services/projectServices';
import { captureException } from '../../services/logService';
import { Dropdown } from '../dropdown';
import CalendarDropdownMenuV2 from './calendarDropdownMenuV2';
import { observer } from 'mobx-react-lite';
import { rootStore } from '../../mobx/rootStore';
import { TaskModel } from '../../mobx/models/task';
import Chip from '../chipComponent';
import { BUTTON_TYPE, CHIP_SIZE, CHIP_TYPE } from '../../global/constants';
import Button from '../button';

interface Props {
  data?: TaskModel;
  loadData?: () => void;
  isCreateTask?: boolean;
  isShareDocModel?: boolean;
  isRecurringTask?: boolean;
}

const CalendarPopUp: React.FC<Props> = (props) => {
  const { isCreateTask, isRecurringTask, isShareDocModel, data, loadData } = props;
  //States
  const [calendarDate, setCalendarDate] = useState(new Date());
  // const [displayState, setDisplayState] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Ref variables
  const calenderDropdownRef = useRef<{
    closeDropdown: () => void;
  }>(null);

  //Mobx store variables
  const { taskStore, currentUser, projectStore } = rootStore;
  const { createTaskInput } = taskStore;
  const { docData } = projectStore;
  //Other variables
  const dateToString = createTaskInput?.dueDate;
  const dateOnly = dayjs(dateToString).format(currentUser?.dateFormat);

  const closeDropdown = useCallback(() => {
    calenderDropdownRef?.current?.closeDropdown();
  }, []);

  useEffect(() => {
    if (isCreateTask && createTaskInput?.dueDate) {
      setCalendarDate(new Date(createTaskInput.dueDate));
    } else if (data?.dueDate) {
      setCalendarDate(new Date(data.dueDate));
    }
  }, [data?.dueDate, isCreateTask, createTaskInput?.dueDate]);

  const handleSetLinkDueDate = (value: string | undefined) => {
    rootStore.projectStore.updateDocData('expiryDate', value);
    const payload = !value
      ? { UnsetExpireTime: true }
      : {
          ExpiryTime: value
        };
    try {
      if (docData?.id) {
        updateDocumentData(docData?.id, payload);
      }
    } catch (error) {
      captureException(error);
    }
  };

  const dropdownContent = useMemo(() => {
    return isShareDocModel ? (
      <Button
        variant={BUTTON_TYPE.SECONDARY}
        size='small'
        iconName='calendar-icon'
        iconPosition='start'
        title={
          docData?.expiryDate ? dayjs(docData?.expiryDate).format(`ddd, ${currentUser?.dateFormat}`) : 'Never expire'
        }
      />
    ) : (
      <Chip
        size={isCreateTask ? CHIP_SIZE.SMALL : CHIP_SIZE.MEDIUM}
        variant={isCreateTask ? CHIP_TYPE.FILL : CHIP_TYPE.EMPTY}
        title={
          isCreateTask
            ? createTaskInput?.dueDate
              ? dateOnly
              : ''
            : data?.dueDate
            ? dayjs(data?.dueDate).format(`ddd, ${currentUser?.dateFormat}`)
            : 'No Due Date'
        }
        leadingIconName='calendar-icon'
        isActive={isDropdownOpen}
      />
    );
  }, [
    isShareDocModel,
    docData?.expiryDate,
    currentUser?.dateFormat,
    isCreateTask,
    createTaskInput?.dueDate,
    dateOnly,
    data?.dueDate,
    isDropdownOpen
  ]);

  return (
    <>
      <Dropdown
        ref={calenderDropdownRef}
        isMinWidth={218}
        content={dropdownContent}
        modalTitle='Due date'
        trigger='click'
        isRemoveMaxHeight={true}
        updateDropdownOpen={(isOpen) => setIsDropdownOpen(isOpen)}
        isCalendarDropdown={true}>
        <>
          <CalendarDropdownMenuV2
            isCreateTask={isCreateTask}
            taskData={data}
            loadData={loadData}
            calendarDate={calendarDate}
            setCalendarDate={setCalendarDate}
            setLinkDueDate={handleSetLinkDueDate}
            isShareDocModel={isShareDocModel}
            isRecurringTask={isRecurringTask}
            closeContextMenu={closeDropdown}
          />
        </>
      </Dropdown>
    </>
  );
};

export default observer(CalendarPopUp);
